import React, { useEffect, useState } from "react";
import {
  getWhatsappMessages_service,
  IWhatsappMessageLog,
} from "./whatsappMessages.service";

interface WhatsappMessageHistoryProps {
  saleDocumentId?: number;
  reservationId?: number;
  /** Cambia para forzar un refetch (ej. tras un envío manual). */
  refreshKey?: number;
}

const TYPE_LABEL: Record<string, string> = {
  confirmacion: "Confirmación de cita",
  recordatorio: "Recordatorio",
  comprobante: "Comprobante",
};

export const WHATSAPP_REASON_LABEL: Record<string, string> = {
  addon_inactive: "Add-on de WhatsApp inactivo",
  not_connected: "Número no conectado",
  template_unavailable: "Plantilla no aprobada por Meta",
  invalid_phone: "Celular inválido",
  customer_opted_out: "El cliente no desea recibir WhatsApp",
  recipient_unreachable: "El número no tiene WhatsApp",
  payment_required: "Meta requiere método de pago",
  rate_limited: "Límite de mensajes alcanzado",
  pdf_failed: "No se pudo generar el PDF",
  send_error: "Error al enviar",
  whatsapp_disabled: "WhatsApp desactivado",
  duplicate: "Envío repetido",
};

const STATUS_META: Record<string, { label: string; icon: string; color: string }> = {
  pendiente: { label: "Pendiente", icon: "…", color: "#6b7280" },
  enviado: { label: "Enviado", icon: "✓", color: "#6b7280" },
  entregado: { label: "Entregado", icon: "✓✓", color: "#0ea5e9" },
  leido: { label: "Leído", icon: "✓✓", color: "#16a34a" },
  fallido: { label: "Falló", icon: "✕", color: "#dc2626" },
  omitido: { label: "No enviado", icon: "–", color: "#d97706" },
};

/** El motivo puede venir con prefijo tipificado: "payment_required [131042]: ...". */
export const translateReason = (reason: string | null): string | null => {
  if (!reason) return null;
  const key = reason.split(/[\s[:]/)[0];
  return WHATSAPP_REASON_LABEL[key] || WHATSAPP_REASON_LABEL[reason] || null;
};

const formatDate = (value: string | null): string => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("es-PE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Lima",
  });
};

const WhatsappMessageHistory = ({
  saleDocumentId,
  reservationId,
  refreshKey = 0,
}: WhatsappMessageHistoryProps) => {
  const [items, setItems] = useState<IWhatsappMessageLog[]>([]);

  useEffect(() => {
    if (!saleDocumentId && !reservationId) return;
    let active = true;
    getWhatsappMessages_service({ saleDocumentId, reservationId }).then(
      (data) => {
        if (active) setItems(data);
      }
    );
    return () => {
      active = false;
    };
  }, [saleDocumentId, reservationId, refreshKey]);

  if (items.length === 0) return null;

  return (
    <div
      data-testid="whatsapp-message-history"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: "8px 12px",
        margin: "12px 0",
        fontSize: 13,
      }}
    >
      <p style={{ fontWeight: 600, margin: "0 0 6px" }}>Mensajes de WhatsApp</p>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {items.map((m) => {
          const status = STATUS_META[m.status] || {
            label: m.status,
            icon: "•",
            color: "#6b7280",
          };
          const reason = translateReason(m.reason);
          const who =
            m.trigger === "manual"
              ? `Enviado por ${m.employeeName || "un usuario"}`
              : "Automático";
          return (
            <li
              key={m.id}
              data-testid="whatsapp-message-item"
              style={{ padding: "4px 0", borderTop: "1px solid #f3f4f6" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span>
                  {TYPE_LABEL[m.type] || m.type} · {who}
                  {m.to ? ` · ${m.to}` : ""}
                </span>
                <span style={{ color: status.color, whiteSpace: "nowrap" }}>
                  {status.icon} {status.label}
                </span>
              </div>
              <div style={{ color: "#6b7280", fontSize: 12 }}>
                {formatDate(m.statusAt || m.sentAt)}
                {reason ? ` · ${reason}` : ""}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default WhatsappMessageHistory;
